import React, { useCallback, useMemo } from 'react';
import { useExpanded, useTable } from 'react-table';

import { ChevronSmallDown as ChevronSmallDownIcon } from '@styled-icons/entypo/ChevronSmallDown';
import { ChevronSmallRight as ChevronSmallRightIcon } from '@styled-icons/entypo/ChevronSmallRight';
import MdiGroupIcon from 'mdi-react/GroupIcon';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  HStack,
  Icon,
  Box,
} from '@chakra-ui/react';

import Loader from 'components/Loader';

import ComponentsTableRow from './ComponentsTableRow';
import CustomCompositionForm, { FormData } from './CustomCompositionForm';
import { Row, FlattenedComponent } from './types';

type Payload = FormData & {
  id: number;
  code: string;
  observation: string;
  labor_percent: number;
  material_percent: number;
  description: string;
  unit_measure_id: number;
  immobilized: false;
  quantity: number;
};

type Props = {
  data: FlattenedComponent[];
  loading: boolean;
  saving: boolean;
  onEditCustomComposition: (id: number, price: Payload) => Promise<void>;
};

const ComponentsTable: React.FC<Props> = ({
  data,
  loading,
  saving,
  onEditCustomComposition,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: () => null,
        id: 'expander',
        width: '5%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <HStack justifyContent="center" alignItems="center">
              {row.isExpanded ? (
                <Icon as={ChevronSmallDownIcon} />
              ) : (
                <Icon as={ChevronSmallRightIcon} />
              )}

              <Box
                display="flex"
                flexDir="column"
                ml="1"
                justifyContent="center"
                alignItems="center"
                fontWeight="bold"
              >
                {row.original.location}
                <Icon w={4} h={4} as={MdiGroupIcon} />
              </Box>
            </HStack>
          );
        },
      },
      {
        Header: 'Código',
        accessor: 'data.budget_composition_price.code',
        width: '10%',
        Cell: ({ row }: { row: Row }) => {
          return row.original.budget_composition_price?.code || '-';
        },
      },
      {
        Header: 'Descrição',
        accessor: 'data.budget_composition_price.description',
        width: '70%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <Text
              py={1}
              maxWidth={{ base: '100%', md: '650px' }}
              whiteSpace="pre-wrap"
            >
              {row.original.budget_composition_price?.description || '- '}
            </Text>
          );
        },
      },
      {
        Header: 'Unidade',
        accessor: 'data.budget_composition_price.unit_measure_id.description',
        width: '10%',
        Cell: ({ row }: { row: Row }) => {
          return (
            row.original.budget_composition_price?.unit_measure_id
              .description || '-'
          );
        },
      },
      {
        Header: 'Qtde',
        accessor: 'data.quantity',
        width: '10%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return Intl.NumberFormat('pt-BR', {
            maximumFractionDigits: 4,
          }).format(row.original.quantity);
        },
      },
      {
        Header: 'Unit.',
        accessor: 'data.social_law_price',
        width: '10%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(row.original.social_law_price || 0);
        },
      },
      {
        Header: 'BDI',
        accessor: 'data.bdi_percent',
        width: '10%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return `${Intl.NumberFormat('pt-BR', {
            maximumFractionDigits: 2,
          }).format(row.original.bdi_percent || 0)}%`;
        },
      },
      {
        Header: 'Com BDI',
        accessor: 'data.price',
        width: '10%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(row.original.price || 0);
        },
      },
      {
        Header: 'Total',
        accessor: 'data.total',
        width: '10%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(row.original.total || 0);
        },
      },
    ],
    [],
  );

  const getRowId = useCallback((row) => {
    return row.id;
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable(
    {
      // @ts-expect-error types
      columns,
      data,
      getRowId,
      autoResetExpanded: false,
    },
    useExpanded,
  );

  const renderRowSubComponent = useCallback(
    ({ component: item }) => (
      <CustomCompositionForm
        initialData={{
          type: 'separate',
          code: item.budget_composition_price.code,
          description: item.budget_composition_price.description,
          unit_measure_id: item.budget_composition_price.unit_measure_id?.id,
          unit_price: item.budget_composition_price.social_law_price,
          labor: item.budget_composition_price.labor,
          material: item.budget_composition_price.material,
          equipment: item.budget_composition_price.equipment,
          third_party: item.budget_composition_price.third_party_services,
          other: item.budget_composition_price.other,
          quantity: item.quantity,
          total_value: item.budget_composition_price.total,
        }}
        onSubmit={(price) => {
          onEditCustomComposition(item.id, {
            id: item.budget_composition_price.id,
            observation: item.budget_composition_price.observation,
            labor_percent: item.labor_percent,
            material_percent: item.material_percent,
            immobilized: false,
            ...price,
          });
        }}
        isSaving={saving}
      />
    ),
    [onEditCustomComposition, saving],
  );

  return (
    <TableContainer width="100%">
      <Table {...getTableProps()} borderBottom="none" variant="simple">
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th
                  {...column.getHeaderProps()}
                  width={column.width}
                  // @ts-expect-error isNumeric does not exists on lib type
                  isNumeric={column.isNumeric}
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>

        <Tbody {...getTableBodyProps()}>
          {!loading &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);

              return (
                <ComponentsTableRow
                  row={row as unknown as Row}
                  visibleColumns={visibleColumns}
                  renderRowSubComponent={renderRowSubComponent}
                  {...row.getRowProps()}
                />
              );
            })}

          {loading && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
              >
                <Loader />
              </Td>
            </Tr>
          )}

          {!loading && data.length === 0 && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
                className="text-center"
              >
                Nenhum resultado encontrado.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default ComponentsTable;
